import { MenuItemDef, GetContextMenuItemsParams, IRowNode, GridApi } from '@ag-grid-community/core';
import { ICellRendererParams } from '@ag-grid-community/core';

import { MESSAGE_CHANGE_SECTION, MESSAGE_CREATE_KIT, MESSAGE_HIGHLIGHT_UNHIGHLIGHT, ROW_LEVEL } from './constants';
import { handleRightClickClearSelection, handleRightClickPasteOperation } from '../pricing&LeadTime/shared/utils';

const withClearRangeSelection = (action: (() => Promise<void>) | (() => void), api: GridApi) => {
    return async () => {
        try {
            await action();
        } finally {
            api?.clearRangeSelection();
        }
    };
};

export const getTableRightClickOptions = (props: GetContextMenuItemsParams) => {
    const subMenuResult = getSingleRowOptions(props as unknown as ICellRendererParams);
    const { data } = props.node as IRowNode;

    const { onAddBelow, setSelectedItems, selectedRowsRef, createNewRowsFromClipboard, addNewRowsToTheTable, tableRef, toRemoveColumnList = [] } = props?.context;
    const propsNeededForPayload = { ...props, data };
    const getSelectedNodes = () => {
        const cellRanges = props.api?.getCellRanges();
        if (cellRanges && cellRanges.length > 0) {
            return cellRanges.flatMap((range) => {
                const row1 = range.startRow?.rowIndex ?? 0;
                const row2 = range.endRow?.rowIndex ?? row1;
                const minRow = Math.min(row1, row2);
                const maxRow = Math.max(row1, row2);
                const nodes = [];
                for (let i = minRow; i <= maxRow; i++) {
                    const node = props.api?.getDisplayedRowAtIndex(i);
                    if (node) nodes.push(node);
                }
                return nodes;
            });
        }
        return [];
    };

    const totalNodeSelected = getSelectedNodes();

    const selectedData = [...new Set((getSelectedNodes() || []).filter((node) => node.level !== 0).map((data) => data.data))];
    const multipleSubMenuResult = getMultipleRowOptions(props as unknown as ICellRendererParams) || [];

    setSelectedItems(selectedData);

    const getSelectedNodesInfo = (nodes: IRowNode[]) => {
        return nodes.reduce(
            (acc, node) => {
                if (node.level === ROW_LEVEL.SECTION) {
                    acc.hasSections = true;
                } else if (node.level === ROW_LEVEL.PRODUCT) {
                    acc.hasProducts = true;
                } else if (node.level === ROW_LEVEL.COMPONENT) {
                    acc.hasComponents = true;
                }
                return acc;
            },
            { hasProducts: false, hasComponents: false, hasSections: false }
        );
    };

    const { hasSections, hasProducts, hasComponents } = getSelectedNodesInfo(totalNodeSelected);

    const result: (string | MenuItemDef)[] = [
        'copy',
        'cut',
        {
            name: 'Paste',
            shortcut: 'Ctrl+V',
            action: async () => {
                const selectedRanges = props.api?.getCellRanges() || [];
                if (selectedRanges.length > 0) {
                    const column = selectedRanges[0].columns[0];
                    const fieldName = column.getColId();
                    addNewRowsToTheTable(selectedRowsRef, tableRef, fieldName, createNewRowsFromClipboard, toRemoveColumnList);
                    await handleRightClickPasteOperation({
                        api: props.api,
                        context: props.context,
                        selectedRanges: props.api?.getCellRanges() || []
                    });
                }
            }
        },
        {
            name: 'Clear Contents',
            action() {
                handleRightClickClearSelection({ api: props.api, context: props.context, selectedRanges: props.api?.getCellRanges() || [], getSelectedNodes: getSelectedNodes });
            },
            shortcut: 'Delete'
        },
        'separator'
    ];
    if (props?.node?.level !== ROW_LEVEL.SECTION) {
        result.push({
            name: 'Add Product below ',
            shortcut: 'Ctrl+I',
            action: withClearRangeSelection(() => onAddBelow?.(propsNeededForPayload), props.api)
        });
    }

    result.push({
        name: 'Row Actions',
        subMenu: totalNodeSelected.length === 1 ? subMenuResult : multipleSubMenuResult || [],
        disabled: false
    });

    if (totalNodeSelected?.length > 1 && [hasSections, hasProducts, hasComponents].filter(Boolean).length === 1) {
        result.splice(6, 0, {
            name: `Add ${totalNodeSelected.length} Products below `,
            action: withClearRangeSelection(() => {
                getSelectedNodes().forEach((node) => {
                    onAddBelow?.(propsNeededForPayload);
                });
            }, props.api)
        });
    }
    return result;
};

const getSingleRowOptions = (props: ICellRendererParams): (string | MenuItemDef)[] => {
    const {
        isOptionTable,
        onAddBelow,
        onClickMoveSection,
        onDuplicate,
        onHide,
        onClickDelete,
        handleAddOption,
        approveOption,
        onProductMoveIn,
        onHighlight,
        onProductMoveOut,
        onCopyProductDataToKit,
        lotMap
    } = props?.context;
    const { data, level } = props?.node;

    const { id, category, manufacturer, model_number, modelNo, product_to_replace, replaceProductId, is_hidden, is_kit, type, is_highlighted } = data || {};

    let options: (string | MenuItemDef)[] = [];

    const propsNeededForPayload = { ...props, data };

    if (level === ROW_LEVEL.SECTION) {
        options = [
            {
                name: 'Hide',
                action: withClearRangeSelection(() => onHide?.(propsNeededForPayload, true), props.api)
            },
            {
                name: 'Unhide',
                action: withClearRangeSelection(() => onHide?.(propsNeededForPayload, false), props.api)
            },
            { name: 'Delete', action: withClearRangeSelection(() => onClickDelete?.(propsNeededForPayload), props.api), cssClasses: ['red'] }
        ];
    } else if (level === ROW_LEVEL.PRODUCT) {
        options = [
            {
                name: isOptionTable ? 'Add Option Below' : 'Add Product Below',
                action: withClearRangeSelection(() => onAddBelow?.(propsNeededForPayload), props.api),
                shortcut: 'Ctrl+I'
            },
            { name: 'Change Section', action: withClearRangeSelection(() => onClickMoveSection?.(propsNeededForPayload), props.api) },
            'separator',
            {
                name: is_hidden ? 'Unhide' : 'Hide',
                action: withClearRangeSelection(() => onHide?.(propsNeededForPayload, is_hidden), props.api)
            },
            { name: 'Duplicate', action: withClearRangeSelection(() => onDuplicate?.(propsNeededForPayload), props.api) },
            { name: 'Delete', action: withClearRangeSelection(() => onClickDelete?.(propsNeededForPayload), props.api), cssClasses: ['red'] }
        ];

        if (!isOptionTable) {
            const toDisableAddOption = Boolean(lotMap[id]) || is_kit || !(category || manufacturer || model_number || modelNo || type);
            options.splice(1, 0, { name: 'Add Option', action: withClearRangeSelection(() => handleAddOption?.(propsNeededForPayload), props.api), disabled: toDisableAddOption }, 'separator');
        } else {
            options.splice(1, 0, { name: 'Approve Option', action: withClearRangeSelection(() => approveOption?.(id), props.api), disabled: !(product_to_replace || replaceProductId) });
        }

        if (!is_kit) {
            onProductMoveIn && options.splice(3, 0, { name: 'Move to kit', action: withClearRangeSelection(() => onProductMoveIn(id, data?.groupPath[0]), props.api) });
            onHighlight && options.splice(6, 0, { name: is_highlighted ? ' Unhighlight' : 'Highlight', action: withClearRangeSelection(() => onHighlight([id]), props.api) }, 'separator');
        }
    } else if (level === ROW_LEVEL.COMPONENT) {
        options = [
            { name: 'Add Product Below', action: withClearRangeSelection(() => onAddBelow?.(propsNeededForPayload), props.api), shortcut: 'Ctrl + I' },
            { name: 'Copy Details to Main Line', action: withClearRangeSelection(() => onCopyProductDataToKit?.(propsNeededForPayload), props.api) },
            'separator',
            { name: 'Remove from kit', action: withClearRangeSelection(() => (onProductMoveOut ? onProductMoveOut(propsNeededForPayload) : null), props.api) },
            'separator',
            {
                name: is_hidden ? 'Unhide' : 'Hide',
                action: withClearRangeSelection(() => (onHide ? onHide(propsNeededForPayload, is_hidden) : null), props.api)
            },
            { name: 'Duplicate', action: withClearRangeSelection(() => (onDuplicate ? onDuplicate(propsNeededForPayload) : null), props.api) },
            { name: 'Delete', action: withClearRangeSelection(() => (onClickDelete ? onClickDelete(propsNeededForPayload) : null), props.api), cssClasses: ['red'] }
        ];
    }

    return options;
};

const getMultipleRowOptions = (props: ICellRendererParams): (string | MenuItemDef)[] => {
    const { onCreateKit, onHighlightMultiple, isHighlightDisabled, isChangeSectionDisabled, isCreateKitDisabled, onHideMultiple, onMove, onDuplicateMultiple } = props?.context;
    let options: (string | MenuItemDef)[] = [];

    options = [
        {
            name: 'Create Kit',
            action: withClearRangeSelection(() => onCreateKit?.(), props.api),
            tooltip: isCreateKitDisabled ? MESSAGE_CREATE_KIT : '',
            disabled: isCreateKitDisabled
        },

        {
            name: 'Change Section',
            action: withClearRangeSelection(() => onMove?.(), props.api),
            tooltip: isChangeSectionDisabled ? MESSAGE_CHANGE_SECTION : '',
            disabled: isChangeSectionDisabled
        },

        {
            name: 'Highlight / Unhighlight',
            action: withClearRangeSelection(() => onHighlightMultiple(), props.api),
            disabled: isHighlightDisabled,
            tooltip: isHighlightDisabled ? MESSAGE_HIGHLIGHT_UNHIGHLIGHT : ''
        },
        {
            name: 'Hide / Unhide',
            action: withClearRangeSelection(() => onHideMultiple(), props.api)
        },
        {
            name: 'Duplicate',
            action: withClearRangeSelection(() => onDuplicateMultiple(), props.api)
        }
        // commenting now in future we can add it, so keeping the code here .
        // {
        //     name: 'Delete',
        //     action: withClearRangeSelection(() => onClickMultipleDelete(), props.api),
        //     cssClasses: ['red']
        // }
    ];

    return options;
};
